import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, FileText, LayoutDashboard } from "lucide-react";

export default function Payment({ status }) {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [rec, setRec] = useState(null);
  const [state, setState] = useState("polling"); // polling | paid | pending | error

  useEffect(() => {
    if (status !== "success" || !sessionId) return;
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setRec(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired", "canceled"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        if (cancelled) return;
      }
      tries += 1;
      if (tries >= 8) { setState((s) => (s === "paid" ? s : "pending")); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [status, sessionId]);

  if (status === "cancel") {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-5 py-12 bg-slate-50">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="payment-cancel">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mx-auto"><XCircle className="w-7 h-7" /></span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">Checkout canceled</h1>
          <p className="mt-2 text-slate-600 text-sm">No payment was taken. Your proposal is still saved — you can return and complete checkout whenever you're ready.</p>
          <div className="mt-6 flex flex-col gap-2">
            <Button asChild className="bg-slate-900 hover:bg-slate-800" data-testid="cancel-view-proposals"><Link to="/proposals">Back to proposals</Link></Button>
            <Button asChild variant="outline" data-testid="cancel-home"><Link to="/">Return home</Link></Button>
          </div>
        </div>
      </div>
    );
  }

  const cur = (rec?.currency || "usd").toUpperCase();

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-5 py-12 bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="payment-success">
        {state === "polling" && (
          <>
            <span className="grid place-items-center w-14 h-14 rounded-full bg-slate-100 text-slate-500 mx-auto"><Loader2 className="w-7 h-7 animate-spin" /></span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-slate-600 text-sm">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto"><CheckCircle2 className="w-7 h-7" /></span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900" data-testid="payment-paid-title">Payment confirmed</h1>
            <p className="mt-2 text-slate-600 text-sm">Thank you! We've received your payment{rec?.amount ? ` of ${fmtMoney(rec.amount, cur)}` : ""}. A member of our team will reach out to kick off the work.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild className="bg-slate-900 hover:bg-slate-800" data-testid="paid-view-account"><Link to="/account"><LayoutDashboard className="w-4 h-4 mr-2" /> Go to your account</Link></Button>
              {rec?.proposal_id && <Button asChild variant="outline" data-testid="paid-view-proposal"><Link to={`/proposals/${rec.proposal_id}`}><FileText className="w-4 h-4 mr-2" /> View proposal</Link></Button>}
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <span className="grid place-items-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mx-auto"><Loader2 className="w-7 h-7" /></span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">Payment is processing</h1>
            <p className="mt-2 text-slate-600 text-sm">Your payment is being processed and may take a little longer to confirm. You can safely close this page — we'll update your account and order status automatically.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild className="bg-slate-900 hover:bg-slate-800"><Link to="/account">Go to your account</Link></Button>
            </div>
          </>
        )}
        {state === "error" && (
          <>
            <span className="grid place-items-center w-14 h-14 rounded-full bg-red-100 text-red-600 mx-auto"><XCircle className="w-7 h-7" /></span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">We couldn't confirm this payment</h1>
            <p className="mt-2 text-slate-600 text-sm">No charge may have completed. Please return to your proposal and try again, or contact us if the problem persists.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild variant="outline"><Link to="/proposals"><ArrowLeft className="w-4 h-4 mr-2" /> Back to proposals</Link></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
